import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Menu,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Text,
  mergeClasses,
} from '@fluentui/react-components';
import { ChevronDown20Regular, ChevronUp20Regular, Open20Regular, Person24Regular } from '@fluentui/react-icons';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { Box } from '../../../../theme/fluent';
import deloitteLogo from '../../../../assets/images/logos/Deloitte-logo.webp';
import { COMPONENT_IDS, FEATURE_IDS, PAGE_PATHS } from '../../../../features/app-config/constants/appConstants';
import { useProfilePhoto } from '../../../../hooks/useProfilePhoto';
import { useAppLayoutStyles } from '../../AppLayout.styles';
import { DropdownLinkObject, dropdownLinksArrayPrivacy, dropdownLinksArraySupport, dropdownLinksArraySupportPilot, dropdownLinksArrayPrivacyPilot, LinkCategoryType } from './usefulLinks';
import { useAppConfig } from '../../../../features/app-config';

type MainContentProps = {
  children: React.ReactNode;
};

export const MainContent: React.FC<MainContentProps> = ({ children }) => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [photoEnabled, setPhotoEnabled] = useState(false);
  useEffect(() => {
    setPhotoEnabled(true);
  }, []);
  const { data: profilePhoto } = useProfilePhoto(photoEnabled);
  const { classes, mainContentBackgroundStyle, pureWhiteColor } = useAppLayoutStyles();

  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const currentUser = instance.getActiveAccount();

  // Extract first name from "LastName, FirstName" format
  const firstName = currentUser?.name?.split(',')[1]?.trim() || 'User';

  const { canUseFeature } = useAppConfig();
  const canUseMorningDashboard = canUseFeature(FEATURE_IDS.MORNING_DASHBOARD); 
  const finalPrivacyLinks = useMemo(() => canUseMorningDashboard ? dropdownLinksArrayPrivacy : dropdownLinksArrayPrivacyPilot, [canUseMorningDashboard]);
  const finalSupportLinks = useMemo(() => canUseMorningDashboard ? dropdownLinksArraySupport : dropdownLinksArraySupportPilot, [canUseMorningDashboard]);

  return (
    <Box className={classes.mainRoot}>
      {/* Top Header Bar */}
      <Box px="l" py="m" className={classes.mainHeaderTop}>
        <Box display="flex" alignItems="center" gap="m">
          <img width={85} height={16} src={deloitteLogo} className={classes.brandLogoImage} alt="Deloitte Logo" />
          <Box className={classes.brandSeparator} />
          <Text size={400} weight="semibold" className={classes.brandText} style={pureWhiteColor}>
            Robin
          </Text>
        </Box>

        <Box display="flex" alignItems="center" gap="m">
          {/* User Menu */}
          <Menu
            open={userMenuOpen}
            onOpenChange={(_, data) => setUserMenuOpen(data.open)}
            positioning={{ position: 'below', align: 'end', offset: { mainAxis: 6 } }}
          >
            <MenuTrigger disableButtonEnhancement>
              <Box
                className={classes.userMenuTrigger}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Avatar
                  name={currentUser?.name || 'User'}
                  size={32}
                  color="brand"
                  icon={<Person24Regular />}
                  image={profilePhoto?.photo ? { src: `data:image/jpeg;base64,${profilePhoto.photo}` } : undefined}
                />
                <Text size={300} style={pureWhiteColor}>
                  Welcome {firstName}
                </Text>
                {userMenuOpen ? (
                  <ChevronUp20Regular style={pureWhiteColor} />
                ) : (
                  <ChevronDown20Regular style={pureWhiteColor} />
                )}
              </Box>
            </MenuTrigger>
            <MenuPopover style={{ minWidth: '244px', borderRadius: '12px' }}>
              <MenuList>
                <MenuItem
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate(`${PAGE_PATHS.DASHBOARD}?panel=${COMPONENT_IDS.MY_ACCOUNT_PANEL}`);
                  }}
                >
                  My Account
                </MenuItem>
                {finalPrivacyLinks ?.map((item) => (
                  <ContentMenuItem key={item.label ?? item.url} {...item} />
                ))}
                <MenuDivider />
                <MenuGroup>
                  <MenuGroupHeader>Support</MenuGroupHeader>
                  {finalSupportLinks ?.map((item) => (
                    <ContentMenuItem key={item.label ?? item.url} {...item} />
                  ))}
                </MenuGroup>
              </MenuList>
            </MenuPopover>
          </Menu>
        </Box>
      </Box>

      {/* Main Content */}
      <Box style={mainContentBackgroundStyle} className={mergeClasses(classes.mainContent, 'scrollable-content')}>
        {children}
      </Box>
    </Box>
  );
};


export const ContentMenuItem: React.FC<DropdownLinkObject> = ({ type, url, label }) => {
  const icon = type === LinkCategoryType.support ? <Open20Regular /> : null;
  const onClick = () => window.open(url, '_blank', 'noopener,noreferrer');

  if (!url || !label) return null;

  return (
    <MenuItem icon={icon} onClick={onClick}>
      {label}
    </MenuItem>
  );
};